<template>
  <div :class="appClasses">
    <Loading v-if="appIsLoading" />
    <div v-if="!appIsLoading" class="layout layout--default">
      <MaintenanceMessage
        v-if="hasMaintenanceMessage"
        icon="⚠"
        :text="appStore.maintenanceMessage"
      />
      <AppHeader v-if="showNavigation" />
      <div
        :class="{
          'layout__content': true,
          'layout__content--nav': showNavigation,
        }"
      >
        <router-view />
      </div>
      <BottomNav v-if="appInitialized && showNavigation" />
      <transition name="fade">
        <div
          :class="{
            'popup-overlay': true,
            'popup-overlay--visible': overlayVisible,
          }"
          @click="closeModalOrSlidein()"
        ></div>
      </transition>
      <portal-target name="modal-target"></portal-target>
    </div>
    <MenuSlidein v-if="appInitialized" />
    <div class="slidein-overlay" @click="closeSlideins()"></div>

    <NewVersionPopup v-if="newVersionPopup.isVisible.value" />
    <BasePricePopup v-else-if="basePricePopup.isVisible.value" />
    <NotificationsRequestPopup v-else-if="notificationsRequestPopup.isVisible.value" />
    <ConfirmEmailPopup v-else-if="confirmEmailPopup.isVisible.value" />
  </div>
</template>

<script setup lang="ts">
  import AppHeader from '@/components/AppHeader.vue';
  import BottomNav from '@/components/BottomNav.vue';
  import Loading from '@/components/Loading.vue';
  import MaintenanceMessage from '@/components/MaintenanceMessage.vue';
  import MenuSlidein from '@/components/slidein/MenuSlidein.vue';
  import { useAppStore } from '@/stores/useAppStore';
  import { usePopup } from '@/composables/usePopup.ts';
  import { usePopupOverlay } from '@/composables/usePopupOverlay.ts';
  import { useSpecificPopup } from '@/composables/useSpecificPopup.ts';
  import { useStrings } from '@/composables/useStrings.ts';
  import { computed, defineAsyncComponent, onMounted, watch } from 'vue';
  import { useRoute, useRouter } from 'vue-router';
  import { storeToRefs } from 'pinia';
  import { useCubeStore } from '@/stores/useCubeStore';
  import { useMapStore } from '@/stores/useMapStore';
  import { useNotificationsStore } from '@/stores/useNotificationsStore';
  import { useCurrentReadsStore } from '@/stores/useCurrentReadsStore';
  import { useAccountStore } from './stores/useAccountStore';

  const BasePricePopup = defineAsyncComponent(() => import('@/components/popups/BasePricePopup.vue'))
  const NotificationsRequestPopup = defineAsyncComponent(() => import('@/components/popups/NotificationsRequestPopup.vue'))
  const NewVersionPopup = defineAsyncComponent(() => import('@/components/popups/NewVersionPopup.vue'))
  const ConfirmEmailPopup = defineAsyncComponent(() => import('@/components/popups/ConfirmEmailPopup.vue'))

  const router = useRouter()
  const currentRoute = useRoute()
  const appStore = useAppStore()
  const cubeStore = useCubeStore()
  const mapStore = useMapStore()
  const accountStore = useAccountStore()
  const notificationsStore = useNotificationsStore()
  const { isSlideinVisible } = storeToRefs(appStore)
  const { t } = useStrings()
  const { activeModal, hidePopup, getPopup } = usePopup()

  const newVersionPopup = useSpecificPopup('newVersion')
  const basePricePopup = useSpecificPopup('basePrice')
  const notificationsRequestPopup = useSpecificPopup('notificationsRequest')
  const confirmEmailPopup = useSpecificPopup('confirmEmail')
  const cubeNotReachablePopup = useSpecificPopup('cubeNotReachable')

  const currentReadsStore = useCurrentReadsStore()

  // We need to watch this in App.vue to allow tracking changes on all pages.
  const isCubeReachable = computed(() => currentReadsStore.isCubeReachable)
  watch(isCubeReachable, (newValue) => {
    if (!newValue) {
      cubeNotReachablePopup.show()
    } else {
      cubeNotReachablePopup.hide()
    }
  })
  const isCommunityless = computed(() => accountStore.properties.isCommunityless)
  watch(isCommunityless, (newValue) => {
    if (newValue) {
      cubeNotReachablePopup.show()
    } else {
      cubeNotReachablePopup.hide()
    }
  })

  const hasMaintenanceMessage = computed(() => (appStore.maintenanceMessage?.length ?? 0) > 0)
  const showNavigation = computed(() => appStore.showNavigation)
  const appIsLoading = computed(() => appStore.isLoading)
  const appInitialized = computed(() => appStore.appInitialized)

  const { overlayVisible } = usePopupOverlay('global')


  const closeModalOrSlidein = () => {
    if (activeModal.value) {
      const popup = getPopup(activeModal.value)
      if (popup && popup.canBeClosedByUser) {
        // Note that we're not closing regular non-modal popups since it's
        // possible that more than one is visible and possibly the slidein
        // menu as well. Since we're not forcing the user the use a non-modal
        // popup one can't expect that pressing esc closes the correct or any
        // popup that is non-modal.
        hidePopup(activeModal.value)
      }
    }
    else {
      closeSlideins()
    }
  }

  const closeSlideins = () => {
    appStore.hideAllSlideins()
  }

  const longPolling = () => {
    // PG: This was changed by choice from dispatching 'loadProducerPrices'
    // TODO APP-572 But why?
    console.debug('refreshing app for latest prices etc')
    appStore.refresh({ router })

    setTimeout(
      () => window.requestAnimationFrame(longPolling),
      10 * 60 * 1000,
    )
  }

  const appClasses = computed(() => {
    let classes = ['app', 'app--' + currentRoute.name?.toString()]
    if (isSlideinVisible.value()) {
      classes.push('app--slidein-visible')
    }
    return classes.join(' ')
  })

  const initializePushNotifications = () => {
    // if (!localStorage.getItem('notificationsChoice')) {
    //   store.commit('setPopupVisible', {
    //     context: 'global',
    //     name: 'notificationsRequest',
    //     visible: true,
    //   });
    // }

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (e) => {
        if (e.data.type === 'onpush') {
          cubeStore.addNotification(e.data.data)

          switch (e.data.data.type_name) {
            case 'consumer_connected':
            case 'consumer_disconnected':
              cubeStore.updateCubeConnections()
              break

            case 'price_change':
              // update the price for this connection
              mapStore.loadProducerPrices()
          }
        }
        else if (e.data.type === 'changeUrl') {
          const parts = e.data.data.split('#')
          router.push({
            path: parts[0],
            hash: parts[1],
          })
        }
      })

      navigator.serviceWorker.addEventListener(
        'pushsubscriptionchange',
        (event: any) => {
          event.waitUntil(notificationsStore.renewSubscription())
        },
        false
      )
    }
  }

  onMounted(() => {
    initializePushNotifications()

    // The user seems to be logged in and has a selected a cube. Lets start.
    if (!appStore.appInitialized) {
      appStore.init({ router })
    }

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        closeModalOrSlidein()
      }
    })

    // TODO Why initialize longpolling twice?
    setTimeout(
      () => window.requestAnimationFrame(longPolling),
      60 * 10 * 1000
    )
  })
</script>

<style lang="scss">
@use 'scss/partials/css-custom-properties';
// We're using bootstrap 4.1's reboot to normalize styles sensibly.
@use 'scss/partials/reboot';
@use 'scss/partials/page';
@use 'scss/partials/general-styles';
@use 'scss/partials/forms';
@use 'scss/partials/input-range';
@use 'scss/partials/button';

.app {
  width: 100vw;
  height: 100vh;

  &__version {
    position: absolute;
    bottom: $spacer10;
    left: 50%;
    transform: translateX(-50%);
    opacity: 0.5;
  }
}

.layout {
  position: fixed;
  top: 0;
  width: 100%;
  right: 0;
  left: 0;
  bottom: var(--safe-area-inset-bottom);

  display: flex;
  flex-direction: column;

  &__header {
    margin: 0;
    padding: $spacer05 $spacer05 $spacer025;
    display: flex;
    justify-content: space-between;
    align-items: center;
    transition: $transition-base;
    flex-shrink: 0;
    background: $body-bg;

    &--above-overlay {
      z-index: $popup-overlay-z-index + 1;
    }

    @media (min-width: $bp-sm) {
      padding: $spacer10;
    }
  }

  &__content {
    flex-grow: 1;
    overflow-y: auto;
    padding-left: $spacer05;
    padding-right: $spacer05;
    // To allow content inside to be positioned absolutely, like the history
    // page.
    position: relative;

    &--nav {
      margin-bottom: $nav-height;

      @media (min-width: $bp-sm) {
        margin-bottom: $nav-height-sm;
      }
    }
  }
}

.logo {
  max-width: $logo-max-width;
  display: block;
  transition: $transition-base;
  margin: $spacer10 auto $spacer20;

  @media (min-height: 260px) {
    margin-top: $spacer30;
    max-width: 200px;
  }

  img {
    max-width: 100%;
    width: 100%;
  }
}

.popup-overlay {
  background: rgba(0, 0, 0, 0.3);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  transition: all 0.8s ease 0s;
  opacity: 0;
  z-index: $popup-overlay-z-index;
  visibility: hidden;

  &--visible {
    visibility: visible;
    opacity: 1;
  }

  &.fade-leave-active {
    opacity: 0;
  }
}
</style>
